import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { Order_Status_Enum, TaskAction } from '@kheops/graphql';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom, useAtomValue } from 'jotai';
import { format } from 'date-fns';
import { orderAtom, orderDeliveryDateAtom } from '../state/state';
import DeliveryDatePicker from '../../common/components/DeliveryDatePicker';
import { longDateFormat } from '../../common/state/state';

export type DeliveryDateOrderStatus =
  Order_Status_Enum.OrderToBeValidatedBySupplier
  | Order_Status_Enum.OrderToBeValidatedByBuyer
  | Order_Status_Enum.OrderToBeDeliveredBySupplier
  | Order_Status_Enum.OrderToBePreparedBySupplier;

interface OrderDeliveryDateDialogProps {
  open: boolean;
  onClose: (date?: Date | null) => void;
  orderStatus: DeliveryDateOrderStatus;
  actionType: Exclude<TaskAction, TaskAction.Cancel | TaskAction.Reject>;
  orderShouldBeRescheduled: boolean;
}

const deliveryDateDialogWordingMap : {
  [action in Exclude<TaskAction, TaskAction.Cancel | TaskAction.Reject>]: {
  [status in DeliveryDateOrderStatus]: {
  title: string;
  description: string;
}}} = {
  APPROVE: {
    order_to_be_validated_by_supplier: {
      title: 'order:validate_order',
      description: 'order:confirm_delivery_date_message',
    },
    order_to_be_validated_by_buyer: {
      title: 'order:validate_order',
      description: 'order:confirm_delivery_date_message',
    },
    order_to_be_delivered_by_supplier: {
      title: 'order:confirm_delivery',
      description: 'order:confirm_delivery_message',
    },
    order_to_be_prepared_by_supplier: {
      title: 'order:confirm_delivery',
      description: 'order:confirm_delivery_message',
    },
  },
  MODIFY: {
    order_to_be_delivered_by_supplier: {
      title: 'order:reschedule_the_delivery',
      description: 'order:reschedule_the_delivery_description',
    },
    order_to_be_validated_by_supplier: {
      title: 'order:validate_order',
      description: 'order:confirm_delivery_date_message',
    },
    order_to_be_validated_by_buyer: {
      title: 'order:reschedule_delivery',
      description: 'order:reschedule_delivery_message',
    },
    order_to_be_prepared_by_supplier: {
      title: 'order:reschedule_delivery',
      description: 'order:reschedule_delivery_message',
    },
  },
};

export default function OrderDeliveryDateDialog({ open, onClose, orderStatus, actionType, orderShouldBeRescheduled }: OrderDeliveryDateDialogProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'order']);
  const { contract: { buyingCompanyId } } = useAtomValue(orderAtom);
  const [shouldShowErrorMessage, setShouldShowErrorMessage] = useState(false);
  const [deliveryDate, setDeliveryDate] = useAtom(orderDeliveryDateAtom);
  const [initialDate, setInitialDate] = useState(deliveryDate);

  const handleCancel = (): void => {
    onClose();
    setDeliveryDate(initialDate);
    setShouldShowErrorMessage(false);
  };

  const handleDateChange = (value: Date | null): void => {
    setDeliveryDate(value);
  };

  const handleConfirm = (): void => {
    if (!deliveryDate && orderStatus === Order_Status_Enum.OrderToBeValidatedBySupplier) {
      setShouldShowErrorMessage(true);
      return;
    }
    onClose(deliveryDate);
  };

  const disabledConfirm = useMemo((): boolean => {
    return actionType === TaskAction.Modify
      && (
        (orderShouldBeRescheduled && !deliveryDate)
        || (!orderShouldBeRescheduled && (!deliveryDate || !initialDate || format(initialDate, longDateFormat) === format(deliveryDate, longDateFormat)))
      );
  }, [initialDate, deliveryDate]);

  useEffect((): void => {
    setInitialDate(deliveryDate);
  }, [open]);

  return (
    <Dialog
      onClose={handleCancel}
      open={open}
      sx={{
        '& .MuiPaper-root.MuiDialog-paper': {
          minWidth: {
            xs: 'calc(100vw - 64px)',
            md: 640,
          },
        },
      }}
    >
      <DialogTitle>
        <Typography>
          {t(deliveryDateDialogWordingMap[actionType][orderStatus].title)}
        </Typography>
        <Typography>{t(deliveryDateDialogWordingMap[actionType][orderStatus].description)}</Typography>
      </DialogTitle>
      <DialogContent>
        {(orderStatus === Order_Status_Enum.OrderToBeValidatedBySupplier || actionType === TaskAction.Modify) && (
          <>
            <Typography variant="titleMedium" sx={{ mb: 1 }}>
              {t('order:delivery_date')}
            </Typography>
            <DeliveryDatePicker companyId={buyingCompanyId} value={deliveryDate} onChange={handleDateChange} />
          </>
        )}
      </DialogContent>
      {shouldShowErrorMessage && (<Typography variant="bodySmall" color="error" align="center">{t('order:delivery_date_error_message')}</Typography>)}
      <DialogActions>
        <Button onClick={handleCancel} variant="text">
          {t('common:cancel')}
        </Button>
        <Button onClick={handleConfirm} variant="contained" disabled={disabledConfirm}>
          {t('common:validate')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
